<template>
  <div class="overflow-x-auto select-none" data-testid="page-meetings-availability-grid" @mouseup="endDrag" @mouseleave="endDrag">
    <table class="w-full table-fixed border-collapse">
      <thead>
        <tr>
          <th class="w-1/6 border border-subtle bg-surface-elevation-1 py-2 text-center text-body-s text-subtle">
            {{ t('Meetings.availability.grid.time') }}
          </th>
          <th v-for="track in TRACK_COUNT" :key="track" class="w-1/6 border border-subtle bg-surface-elevation-1 py-2 text-center">
            <span class="text-body-s-bold">{{ t('Meetings.availability.grid.track', { number: track }) }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(slot, row) in timeSlots" :key="slot">
          <td class="border border-subtle py-2 text-center text-body-s text-subtle">{{ slot }}</td>
          <td
            v-for="track in TRACK_COUNT"
            :key="track"
            class="cursor-pointer border border-subtle py-2 text-center"
            :class="isCellOn(row, track - 1) ? 'bg-success-subtle' : 'cell-unavailable'"
            @mousedown.prevent="onMouseDown(row, track - 1)"
            @mouseenter="onMouseEnter(row, track - 1)"
          >
            <SquareCheckBigIcon v-if="isCellOn(row, track - 1)" class="lucide-icon-m text-success" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { SquareCheckBigIcon } from '@ramp106/omrjs-core-ui/icons'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

const TRACK_COUNT = 5

const props = defineProps<{
  modelValue: boolean[][]
  timeSlots: string[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean[][]): void
}>()

const { t } = useI18n()

// ── Helpers ──

/** Clone the entire grid (shallow per row). */
function cloneGrid(): boolean[][] {
  return props.modelValue.map((r) => [...r])
}

/** Return a row with tracks 0..upTo set to true, preserving any already-checked tracks beyond. */
function fillRow(row: boolean[], upTo: number): boolean[] {
  return row.map((existing, t) => (t <= upTo ? true : existing))
}

/** Return a row with tracks 0..(upTo-1) kept, everything from upTo onward cleared. */
function trimRow(row: boolean[], upTo: number): boolean[] {
  return row.map((existing, t) => (t < upTo ? existing : false))
}

function isCellOn(row: number, col: number): boolean {
  return props.modelValue[row]?.[col] ?? false
}

// ── Drag / click state ──

const isDragging = ref(false)
const hasMoved = ref(false)
const startRow = ref(0)
const startCol = ref(0)
let snapshot: boolean[][] = [] // grid state when mousedown fired

function onMouseDown(row: number, col: number) {
  if (!props.modelValue[row]) return

  isDragging.value = true
  hasMoved.value = false
  startRow.value = row
  startCol.value = col
  snapshot = cloneGrid()

  // Immediately paint the clicked row (optimistic — corrected in endDrag if it was a click)
  const grid = snapshot.map((r) => [...r])
  grid[row] = fillRow(grid[row], col)
  emit('update:modelValue', grid)
}

function onMouseEnter(row: number, col: number) {
  if (!isDragging.value) return
  if (row !== startRow.value || col !== startCol.value) hasMoved.value = true

  const rowMin = Math.min(startRow.value, row)
  const rowMax = Math.max(startRow.value, row)
  const colMax = Math.max(startCol.value, col)

  // Rebuild from snapshot — fill every row in the rectangle
  const grid = snapshot.map((r) => [...r])
  for (let r = rowMin; r <= rowMax; r++) {
    grid[r] = fillRow(grid[r], colMax)
  }
  emit('update:modelValue', grid)
}

function endDrag() {
  if (isDragging.value && !hasMoved.value) {
    // Pure click (no mouse movement) — apply click logic instead of drag logic
    const original = snapshot[startRow.value]
    if (original?.[startCol.value]) {
      // Cell was already on → trim this cell and everything to its right
      const grid = cloneGrid()
      grid[startRow.value] = trimRow(original, startCol.value)
      emit('update:modelValue', grid)
    }
    // Cell was off → startDrag already filled it, nothing more to do
  }

  isDragging.value = false
  hasMoved.value = false
}
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.cell-unavailable {
  background: repeating-linear-gradient(-45deg, transparent, transparent 4px, rgb(0 0 0 / 0.04) 4px, rgb(0 0 0 / 0.04) 5px);
}
</style>
